import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import type { RefundDraft, RefundsIntentState } from './types'

/**
 * Намерение окна возврата (срок `screen`): что человек отметил к возврату и
 * какой возврат завершён в этом окне. Сам возврат — на сервере, в кэше RTK
 * Query; здесь его копии нет.
 */
const initialState: RefundsIntentState = { drafts: {}, finished: {} }

export interface ReceiptRef {
  resourceId: string
  receiptId: string
}

/** Ключ черновика: rid первым — одинаковые номера чеков двух AQSI не смешиваются. */
export const receiptKey = (ref: ReceiptRef) => ridKey(ref.resourceId, `receipt:${ref.receiptId}`)

const EMPTY_DRAFT: RefundDraft = { quantities: {} }

const refundsSlice = createSlice({
  name: 'aqsi/refunds',
  initialState,
  reducers: {
    refundQuantityChanged(state, action: PayloadAction<ReceiptRef & { ordinal: number; quantity: string }>) {
      const key = receiptKey(action.payload)
      const draft = state.drafts[key] ?? { quantities: {} }
      const quantity = action.payload.quantity.trim() === '' ? '' : action.payload.quantity.slice(0, 16)
      if (quantity === '') delete draft.quantities[String(action.payload.ordinal)]
      else draft.quantities[String(action.payload.ordinal)] = quantity
      state.drafts[key] = draft
    },
    /** Отметить всё, что ещё можно вернуть (остатки считает selector из ответа сервера). */
    refundQuantitiesFilled(state, action: PayloadAction<ReceiptRef & { quantities: Record<string, string> }>) {
      state.drafts[receiptKey(action.payload)] = { quantities: { ...action.payload.quantities } }
    },
    refundDraftCleared(state, action: PayloadAction<ReceiptRef>) {
      delete state.drafts[receiptKey(action.payload)]
    },
    /** Возврат завершён (чек пробит или возврат отменён): показать итог, а не новую форму. */
    refundFinishedShown(state, action: PayloadAction<ReceiptRef & { refundId: string }>) {
      state.finished[receiptKey(action.payload)] = action.payload.refundId
      delete state.drafts[receiptKey(action.payload)]
    },
    refundFinishedDismissed(state, action: PayloadAction<ReceiptRef>) {
      delete state.finished[receiptKey(action.payload)]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.drafts, action.payload.resourceId)
      dropResource(state.finished, action.payload.resourceId)
    })
  },
})

export const {
  refundQuantityChanged,
  refundQuantitiesFilled,
  refundDraftCleared,
  refundFinishedShown,
  refundFinishedDismissed,
} = refundsSlice.actions
export const refundsReducer = refundsSlice.reducer

export function refundDraftOf(state: RefundsIntentState, ref: ReceiptRef): RefundDraft {
  return state.drafts[receiptKey(ref)] ?? EMPTY_DRAFT
}

export function finishedRefundOf(state: RefundsIntentState, ref: ReceiptRef): string | null {
  return state.finished[receiptKey(ref)] ?? null
}
